<script>
import { useValidation } from '@/hooks/useValidation'
import {
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from '@vue/composition-api'
import { useMessage } from '@/hooks/useMessage'
import { quillEditor } from 'vue-quill-editor'
import { uploadImage } from '@/views/announcement/service'

export default {
  name: 'EditAnnouncement',
  components: { quillEditor },
  props: {
    systemList: {
      type: Array,
      default: () => [],
    },
    rowItem: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const visible = ref(true)
    const formRef = ref()
    const { message } = useMessage()
    const contentLength = ref(0)
    const { required } = useValidation()
    const editorRef = ref()
    const quillInstance = ref(null)
    const UploadRef = ref()
    const file = ref()
    const formData = reactive({
      title: undefined, // 标题
      content: undefined, // 内容
      type: 1, // 公告or站内信
      strength: 2, // 紧急程度
      targetSystem: undefined, // 发布系统
      sourceSystem: 7, // 源系统
      confirmText: '我已知晓',
    })

    // 插入图片
    function insertImage(url) {
      const range = quillInstance.value.getSelection()
      quillInstance.value.insertEmbed(range.index, 'image', url)
    }
    function clear() {
      file.value = undefined
      UploadRef.value.$refs.input.value = ''
    }

    function upload(fileData) {
      const form = new FormData()
      form.append('file', fileData)
      uploadImage(form).then(({ data }) => {
        if (data.status === 200) {
          insertImage(data.data)
        } else message.error(data.message)
        clear()
      }).catch(() => {
        message.error('图片上传失败！')
        clear()
      })
    }
    function checkFileType(fileData) {
      const acceptTypes = '.jpg,.jpeg,.png,.gif,.bmp,.webp'
      const fileType = fileData.name.match(/\.([^.]+)$/)[1]
      const acceptType = acceptTypes.split(',').filter(item => item) || []
      if (acceptType.length === 0) return true

      return acceptType.some(item => item === `.${fileType.toLowerCase()}`)
    }
    function fileChange(val) {
      if (!val) return false

      // 进行格式检验
      const inspect = checkFileType(val)
      if (!inspect) {
        file.value = undefined
        message.error('请上传图片类文件！')
        clear()

        return false
      }

      // 文件大小校验
      if ((file.value.size / 1024 / 1024) > 2) {
        message.warning('上传文件超出2MB限制，请重新选择！')
        file.value = undefined
        clear()

        return false
      }
      upload(val)
    }
    const editorOptions = ref({
      theme: 'snow',
      placeholder: '请输入公告内容',
      modules: {
        toolbar: {
          container: [
            ['bold'], // 加粗
            [{ color: [] }], // 颜色
            [{ list: 'ordered' }],
            ['image'],
          ],
          handlers: {
            image: () => {
              UploadRef.value.$refs.input.click()
            },
          },
        },
      },
    })
    function init() {
      if (JSON.stringify(props.rowItem) !== '{}') {
        formData.title = props.rowItem.title
        formData.content = props.rowItem.content
        formData.targetSystem = props.rowItem.targetSystem
      }
    }
    function handlePaste(event) {
      // 获取剪贴板数据
      const clipboardData = event.clipboardData || window.clipboardData
      const types = clipboardData.types // 获取剪贴板中的所有类型

      // 检查剪贴板中是否包含文件类型
      if (types.includes('Files')) {
        return false
      }
      const diff = contentLength.value // 计算可以粘贴的字符数
      const paste = clipboardData.getData('text') // 获取文本数据
      // 插入粘贴的文本，限制长度
      const textToInsert = paste.substring(0, diff)
      let index = quillInstance.value.selection.savedRange.index
      nextTick(() => {
        setTimeout(() => {
          if (quillInstance.value) {
            // 获取光标位置
            index += document.createTextNode(textToInsert).length
            quillInstance.value.setSelection(index)
          }
        }, 400)
      })
    }
    function onEditorChange({ quill }) {
      // 设置字符长度
      contentLength.value = quill.getLength() - 1
    }
    function removeEmptyParagraphs(html) {
      // 使用正则表达式匹配末尾的 <p><br></p> 以及连续的 <p><br></p>
      const regex = /(<p><br><\/p>\s*)+$/g // 匹配末尾的 <p><br></p> 以及连续的

      return html.replace(/<p><br><\/p>/gi, '').replace(regex, '') // 替换为 ''
    }

    function submit() {
      formRef.value.asyncValidate().then(() => {
        const userData = JSON.parse(localStorage.getItem('userData'))
        const params = {
          ...formData,
          messageId: props.rowItem.messageId,
          sourceUserId: userData.user.userId,
          sourceUserName: userData.user.nickname,
          content: removeEmptyParagraphs(formData.content),
        }
        if (contentLength.value === 0) return message.warning('请输入公告内容')
        visible.value = false
        emit('onSuccess', params)
      })
    }

    init()

    onMounted(() => {
      quillInstance.value = editorRef.value.quill
      quillInstance.value.root.addEventListener('paste', env => {
        if (env.clipboardData && env.clipboardData.files && env.clipboardData.files.length) {
          env.preventDefault()
          const files = Array.from(env.clipboardData.files)

          // 上传服务器并拿到返回的图片地址，插入到富文本中
          console.log(env.clipboardData.files)
          files.forEach(fileData => {
            if (fileData.type.startsWith('image/')) {
              upload(fileData)
            }
          })
        }
      }, true)
      contentLength.value = editorRef.value.quill.getLength() - 1
      quillInstance.value.root.addEventListener('paste', handlePaste, false)
    })

    watch(visible, val => {
      if (!val) {
        emit('close')
      }
    })

    onBeforeUnmount(() => {
      if (quillInstance.value) {
        quillInstance.value.root.removeEventListener('paste', handlePaste, false)
      }
    })

    return {
      visible,
      formRef,
      formData,
      required,
      submit,
      editorOptions,
      editorRef,
      onEditorChange,
      file,
      fileChange,
      UploadRef,
    }
  },
}
</script>

<template>
  <vl-dialog
    :title="rowItem.messageId ? '编辑公告' : '新增公告'"
    :visible.sync="visible"
    width="700px"
    top="0"
  >
    <v-file-input
      ref="UploadRef"
      v-model="file"
      accep=".jpg,.jpeg,.png,.gif,.bmp,.webp"
      style="display: none"
      name="file"
      outlined
      dense
      hide-details="false"
      clearable
      prepend-icon=""
      @change="fileChange"
    />
    <VlForm ref="formRef" label-width="70" layout="left">
      <vl-form-item label="标题" required>
        <v-text-field
          v-model="formData.title"
          v-trim
          :rules="[ required ]"
          placeholder="请输入公告标题"
          dense
          maxlength="50"
          outlined
        />
      </vl-form-item>
      <vl-form-item label="接收系统" required>
        <v-autocomplete
          v-model="formData.targetSystem"
          placeholder="请选择"
          :rules="[required]"
          dense
          outlined
          :items="systemList"
        />
      </vl-form-item>
      <vl-form-item layout="vertical" required label="公告内容">
        <div style="height: 500px; width: 100%;">
          <quill-editor
            ref="editorRef"
            v-model="formData.content"
            :options="editorOptions"
            style="width: 100%; height: 100%"
            @change="onEditorChange"
          />
        </div>
      </vl-form-item>
    </VlForm>
    <template #footer>
      <div class="flex-right gap8">
        <v-btn
          outlined
          @click="visible = false"
        >
          取消
        </v-btn>
        <v-btn
          color="primary"
          @click="submit"
        >
          预览
        </v-btn>
      </div>
    </template>
  </vl-dialog>
</template>

<style scoped lang="scss">

</style>
